import Axios from 'axios';
import { ADMIN_TOKEN_NAME, PORDAL_TOKEN_CONFIG } from 'helpers/const';
import environment from '../environments/environment';
import { authService } from '../services/authService';

const MAX_ERRORS = 5;

let refreshingToken = false;
let refreshPromise = null;

export const ensureValidToken = async () => {
  if (refreshingToken) {
    return refreshPromise; // Return the same promise to prevent multiple refresh calls
  }

  refreshingToken = true;

  const tokenConfigRaw = localStorage.getItem(PORDAL_TOKEN_CONFIG);
  if (tokenConfigRaw) {
    const tokenConfig = JSON.parse(tokenConfigRaw);
    const currentTime = new Date().getTime();
    const expirationTime = tokenConfig.expires_at - 1000 * 60;

    if (expirationTime < currentTime) {
      try {
        refreshPromise = authService.refreshToken(tokenConfig.refresh_token);
        const result = await refreshPromise;

        authService.setPordalConfig({
          ...result.data,
          refresh_token: tokenConfig.refresh_token,
        });

        refreshingToken = false;

        return result.data.access_token;
      } catch (error) {
        authService.logoutProceed();
        refreshingToken = false;

        return null; // Ensure we return null on failure to prevent infinite loops
      }
    }
  }

  refreshingToken = false;

  return null;
};

Axios.interceptors.request.use(
  async (config) => {
    if (
      (config.url.includes(environment.guestyApi) || config.url.includes(environment.guestyApi2)) &&
      !localStorage.getItem(ADMIN_TOKEN_NAME)
    ) {
      const newToken = await ensureValidToken();
      config.headers.Authorization = `Bearer ${newToken || authService.getUserToken()}`;
    }
    if (
      (config.url.includes(environment.guestyApi) || config.url.includes(environment.guestyApi2)) &&
      localStorage.getItem(ADMIN_TOKEN_NAME)
    ) {
      const authKey = btoa(`${environment.guestyApiKey}:${environment.guestyApiSecret}`);
      config.headers.Authorization = `Basic ${authKey}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const specialAxios = Axios.create();
specialAxios.interceptors.request.use(
  (config) => {
    const authKey = btoa(`${environment.guestyApiKey}:${environment.guestyApiSecret}`);
    config.headers.Authorization = `Basic ${authKey}`;

    return config;
  },
  (error) => Promise.reject(error)
);

const errorRequests = [];

// special method for handling guesty erros
const handleError = (error, response) => {
  const errorRequest =
    (error && error.message === 'Network Error') || (response && response.data.err !== undefined);
  const requestUrl = error && error.config ? error.config.url : response.config.url;
  const hadError = errorRequests.find((request) => request.url === requestUrl);
  if (errorRequest && (!hadError || hadError.errors < MAX_ERRORS)) {
    if (hadError) {
      hadError.errors += 1;
    } else {
      errorRequests.push({ url: requestUrl, errors: 2 });
    }

    return true;
  }
  if (errorRequest && hadError.errors >= MAX_ERRORS) {
    clearErrorOnComplete(error || response);

    return false;
  }

  return false;
};

// special method for handling guesty erros
const clearErrorOnComplete = (response) => {
  const hadError = errorRequests.findIndex((request) => request.url === response.config.url);

  if (hadError > -1) {
    errorRequests.splice(hadError, 1);
  }
};

Axios.interceptors.response.use(
  (response) => {
    if (
      response &&
      (response.config.url.includes(environment.guestyApi) ||
        response.config.url.includes(environment.guestyApi2))
    ) {
      if (handleError(undefined, response)) {
        // repeat request if response returns 200, but object err: {} is passed
        return Axios.request(response.config);
      }
      if (response.data.err === undefined) {
        clearErrorOnComplete(response);
      }
    }

    return response;
  },
  (error) => {
    if (
      error.response &&
      error.response.config &&
      (error.response.config.url.includes(environment.guestyApi) ||
        error.response.config.url.includes(environment.guestyApi2))
    ) {
      if (authService.getAdminToken()) {
        authService.authorizeAdmin(authService.getAdminToken());
      }
      if (handleError(error)) {
        // repeat request if response returns message "Network Error" - guesty does not return 5** code
        return Axios.request(error.config);
      }
      if (error.response.config.url && !error.response.config.url.includes('authenticate')) {
        if (error.response.status === 401) {
          authService.logoutProceed();
          window.location.reload();
        }
      }
    }

    return Promise.reject(error);
  }
);

export { specialAxios };
